import React from 'react';
import PropTypes from "prop-types";
import Button from "../form/button/Button";
import PopupWindow from "../popup-window/PopupWindow";
import joinClassNames from "../../common/JoinClassNames";
import MessagePopupPositions from "./MessagePopupPositions";
import MessagePopupTypes from "./MessagePopupTypes";

const propTypes = {
	// the text on the button
	buttonText: PropTypes.string,

	// the text of the cancel button; must have hasCancelButton set to true to show a cancel button
	cancelButtonText: PropTypes.string,

	// CSS class for the PopupWindow
	className: PropTypes.string,

	// duration of the snackbar before it closes
	duration: PropTypes.number,

	// Should a cancel button be shown
	hasCancelButton: PropTypes.bool,

	// a functional component that is an SVG icon
	icon: PropTypes.func,

	// a key to make each message popup unique, this will restart animations, etc.
	keyName: PropTypes.string.isRequired,

	// message to display in the popup
	message: PropTypes.node,

	// the function to call when the popup will close: (pressedButtonText) => { ... }
	onClosePopup: PropTypes.func.isRequired,

	// position that the banner or snackbar will appear (see MessagePopupPositions)
	position: PropTypes.string,

	// what to return to when the popup closes
	returnFocusElement: PropTypes.object,
	
	// the role of the Message Popup. usually "alert" or "alertdialog"
	role: PropTypes.string,
	
	// secondary message that is less important than the main message
	subMessage: PropTypes.node,

	// a larger more prominent title of the popup
	title: PropTypes.node,

	// the type of message popup (see MessagePopupTypes)
	type: PropTypes.oneOf(Object.values(MessagePopupTypes)),

};

const defaultProps = {
	buttonText: 'Okay',
	cancelButtonText: 'Cancel',
	className: undefined,
	duration: 5000,
	hasCancelButton: false,
	icon: undefined,
	message: undefined,
	position: MessagePopupPositions.BOTTOM_LEFT,
	returnFocusElement: undefined,
	role: 'alertdialog',
	subMessage: undefined,
	title: undefined,
	type: MessagePopupTypes.DIALOG,
};

export default class MessagePopup extends React.Component {
	constructor(props) {
		super(props);

		this.startCloseTimer();
		this.closePopup = this.closePopup.bind(this);
	}

	closePopup(pressedButtonText) {
		if (this.closeTimer) {
			clearTimeout(this.closeTimer);
			this.closeTimer = undefined;
		}
		if ((this.props.returnFocusElement) && (this.props.type !== MessagePopupTypes.SNACKBAR)) {
			this.props.returnFocusElement.focus();
		}

		if ((this.props.returnFocusElement) && (this.props.type === MessagePopupTypes.SNACKBAR)) {
			console.warn('returnFocusElement does not work with the SNACKBAR Message Popup Type');
		}

		this.props.onClosePopup(pressedButtonText);
	}

	startCloseTimer() {
		if (this.props.type === MessagePopupTypes.SNACKBAR) {
			const duration = this.props.duration ? this.props.duration : defaultProps.duration;
			this.closeTimer = setTimeout(() => this.closePopup(this.props.buttonText), duration);
		}
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		if (this.props.keyName !== prevProps.keyName) {
			this.startCloseTimer();
		}
	}

	render() {
		const {
			buttonText,
			cancelButtonText,
			className,
			hasCancelButton,
			icon,
			keyName,
			message,
			position,
			role,
			subMessage,
			title,
			type,
		} = this.props;

		let footerChildren = undefined;
		if (!type || type === MessagePopupTypes.DIALOG || type === MessagePopupTypes.BANNER) {
			const buttonName = buttonText ? buttonText : defaultProps.buttonText;
			footerChildren = (
				<div className="atid_message_popup_single_button message-popup__footer">
					{
						hasCancelButton ?
							<Button label={cancelButtonText} className="message-popup__button-cancel" onClick={() => this.closePopup(cancelButtonText)}/>
							: undefined
					}
					<Button label={buttonName} className="message-popup__button-okay" onClick={() => this.closePopup(buttonText)} setInitialFocus={true} />
				</div>
			);
		}

		let onCloseButtonCallback = undefined;
		if (!type || type !== MessagePopupTypes.BANNER) {
			onCloseButtonCallback = this.closePopup;
		}

		const typeClassName = type ? `message-popup--${type}` : `message-popup--${defaultProps.type}`;
		const positionClassName = position ? `message-popup--${position}` : `message-popup--${defaultProps.position}`;

		return (
			<PopupWindow
				key={keyName}
				footerChildren={footerChildren}
				onCloseButtonCallback={onCloseButtonCallback && (() => onCloseButtonCallback(cancelButtonText))}
				backdropClassName={joinClassNames(className, typeClassName, positionClassName)}
				role={role}
			>
				<div className="atid_message_popup_content_wrapper message-popup__content-wrapper">
					{ icon && icon('message-popup__icon') }
					<div className="message-popup__message-wrapper flex1">
						{ title && <div className="message-popup__title">{title}</div> }
						{ message && <div className="message-popup__message">{message}</div> }
						{ subMessage && <div className="message-popup__submessage">{subMessage}</div> }
					</div>
				</div>
			</PopupWindow>
		);
	}
}

MessagePopup.propTypes = propTypes;
MessagePopup.defaultProps = defaultProps;
